import React, { useState, useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext.tsx';
import { 
  isTemporaryByTeamStatus, 
  getTemporaryStaff, 
  formatTemporaryStaffInfo 
} from '../utils/temporaryStaffUtils.ts';

interface TemporaryStaffSelectorProps {
  selectedStaffName?: string;
  selectedTeam?: string;
  onStaffChange: (staffName: string, assignedTeam: string) => void;
  label: string;
  placeholder?: string;
}

export const TemporaryStaffSelector: React.FC<TemporaryStaffSelectorProps> = ({
  selectedStaffName,
  selectedTeam,
  onStaffChange,
  label,
  placeholder = "직원 선택"
}) => {
  const { employees, teams } = useAppContext();
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  
  // 임시직 직원 목록
  const temporaryStaff = useMemo(() => getTemporaryStaff(employees), [employees]);
  
  // 정규직 직원 목록 (팀별)
  const regularStaffByTeam = useMemo(() => {
    const staffByTeam: Record<string, typeof employees> = {};
    employees.filter(emp => !isTemporaryByTeamStatus(emp)).forEach(emp => {
      if (!staffByTeam[emp.team]) {
        staffByTeam[emp.team] = [];
      }
      staffByTeam[emp.team].push(emp);
    });
    return staffByTeam;
  }, [employees]);
  
  // 선택된 직원이 임시직인지 확인
  const selectedEmployee = employees.find(emp => emp.name === selectedStaffName);
  const isSelectedStaffTemporary = selectedEmployee ? isTemporaryByTeamStatus(selectedEmployee) : false;
  
  // 직원 선택 처리
  const handleStaffSelect = (staffName: string) => {
    const employee = employees.find(emp => emp.name === staffName);
    if (!employee) return;
    
    if (isTemporaryByTeamStatus(employee)) {
      // 임시직인 경우 팀 선택 화면 표시
      setShowTeamSelector(true);
    } else {
      // 정규직인 경우 기존 팀으로 자동 배정
      onStaffChange(staffName, employee.team);
    }
  };
  
  // 팀 선택 처리 (임시직용)
  const handleTeamSelect = (teamName: string) => {
    if (selectedStaffName) {
      onStaffChange(selectedStaffName, teamName);
      setShowTeamSelector(false);
    }
  };
  
  return (
    <div className="temporary-staff-selector">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      
      {/* 직원 선택 드롭다운 */}
      <select
        value={selectedStaffName || ''}
        onChange={(e) => handleStaffSelect(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      >
        <option value="">{placeholder}</option>
        
        {/* 임시직 직원들 */}
        {temporaryStaff.length > 0 && (
          <optgroup label="🔄 임시직 (팀 선택 가능)">
            {temporaryStaff.map(emp => (
              <option key={emp.id} value={emp.name}>
                {formatTemporaryStaffInfo(emp)}
              </option>
            ))}
          </optgroup>
        )}
        
        {/* 정규직 직원들 (팀별) */}
        {Object.entries(regularStaffByTeam).map(([teamName, staffList]) => (
          <optgroup key={teamName} label={`🏢 ${teamName}팀`}>
            {staffList.map(emp => (
              <option key={emp.id} value={emp.name}>
                {emp.name} ({emp.position || '직원'})
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      
      {/* 임시직 팀 선택 모달 */}
      {showTeamSelector && isSelectedStaffTemporary && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                📋 {selectedStaffName} 팀 배정
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                임시직 직원이므로 이 건에 대해 배정할 팀을 선택해주세요.
              </p>
              
              <div className="grid grid-cols-1 gap-2">
                {teams.map(team => (
                  <button
                    key={team.id}
                    onClick={() => handleTeamSelect(team.name)}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="font-medium">{team.name}팀</div>
                    <div className="text-sm text-gray-500">
                      목표매출: {team.targetSales?.toLocaleString() || 0}만원
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="flex justify-end mt-4 space-x-2">
                <button
                  onClick={() => setShowTeamSelector(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 선택된 정보 표시 */}
      {selectedStaffName && selectedTeam && (
        <div className="mt-2 p-2 bg-blue-50 rounded-md">
          <div className="text-sm">
            <span className="font-medium">{selectedStaffName}</span>
            {isSelectedStaffTemporary && (
              <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                임시직
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600">
            📍 {selectedTeam}팀에 배정됨
          </div>
        </div>
      )}
    </div>
  );
}; 